import { supabase } from "@/integrations/supabase/client";

const STORAGE_BASE_URL = import.meta.env.VITE_SUPABASE_STORAGE_URL;

export interface ImageTransformOptions {
  width?: number;
  height?: number;
  quality?: number;
  format?: 'webp' | 'avif' | 'origin';
}

/**
 * Generates a public URL for a storage object.
 * If VITE_SUPABASE_STORAGE_URL is set (e.g. in dev), it points to that external project.
 * Otherwise, it uses the default Supabase client's getPublicUrl.
 *
 * @param bucket - The storage bucket name
 * @param path - The file path within the bucket
 * @param transform - Optional image transformation parameters (width, height, quality, format)
 */
export function getStorageUrl(
  bucket: string,
  path: string | null | undefined,
  transform?: ImageTransformOptions
): string | undefined {
  if (!path) return undefined;

  // If the path is already a full URL (external image), return it directly
  // without prepending the storage URL
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }

  let baseUrl: string;

  // Use override if present (e.g. pointing dev to prod buckets)
  if (STORAGE_BASE_URL) {
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    baseUrl = `${STORAGE_BASE_URL}/${bucket}/${cleanPath}`;
  } else {
    // Default behavior
    const { data } = supabase.storage.from(bucket).getPublicUrl(path);
    baseUrl = data.publicUrl;
  }

  // Apply image transformation parameters if provided
  if (transform) {
    const params = new URLSearchParams();

    if (transform.width) {
      params.append('width', transform.width.toString());
    }
    if (transform.height) {
      params.append('height', transform.height.toString());
    }
    if (transform.quality) {
      params.append('quality', transform.quality.toString());
    }
    if (transform.format) {
      params.append('format', transform.format);
    }

    const queryString = params.toString();
    if (queryString) {
      baseUrl += `?${queryString}`;
    }
  }

  return baseUrl;
}

export interface SrcSetOptions {
  widths: number[];
  quality?: number;
  format?: 'webp' | 'avif' | 'origin';
}

/**
 * Generates a srcset string for responsive images.
 * Creates multiple URLs with different widths for optimal image loading.
 *
 * @param bucket - The storage bucket name
 * @param path - The file path within the bucket
 * @param options - Configuration with widths array and optional quality/format
 * @returns A srcset string (e.g., "url1 320w, url2 640w, url3 1024w") or undefined if path is invalid
 *
 * @example
 * ```ts
 * const srcset = generateSrcSet('avatars', 'user/photo.jpg', {
 *   widths: [320, 640, 1024],
 *   quality: 80,
 *   format: 'webp'
 * });
 * // Returns: "https://...?width=320&quality=80&format=webp 320w, ..."
 * ```
 */
export function generateSrcSet(
  bucket: string,
  path: string | null | undefined,
  options: SrcSetOptions
): string | undefined {
  if (!path) return undefined;

  const srcsetParts = options.widths.map((width) => {
    const url = getStorageUrl(bucket, path, {
      width,
      quality: options.quality,
      format: options.format,
    });
    return url ? `${url} ${width}w` : null;
  }).filter((part): part is string => part !== null);

  return srcsetParts.length > 0 ? srcsetParts.join(', ') : undefined;
}

export interface SizesBreakpoint {
  breakpoint?: number; // Max-width in pixels (omit for default size)
  size: string; // CSS size value (e.g., "100vw", "50vw", "300px")
}

/**
 * Generates a sizes attribute string for responsive images based on breakpoints.
 * Creates media queries that tell the browser what size the image will be at different viewport widths.
 *
 * @param breakpoints - Array of breakpoint configurations, ordered from largest to smallest breakpoint
 * @returns A sizes string (e.g., "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw")
 *
 * @example
 * ```ts
 * const sizes = generateSizes([
 *   { breakpoint: 640, size: '100vw' },
 *   { breakpoint: 1024, size: '50vw' },
 *   { size: '33vw' } // default size (no breakpoint)
 * ]);
 * // Returns: "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
 * ```
 */
export function generateSizes(breakpoints: SizesBreakpoint[]): string {
  if (breakpoints.length === 0) {
    return '100vw'; // Default fallback
  }

  const sizesParts = breakpoints.map((bp) => {
    if (bp.breakpoint !== undefined) {
      return `(max-width: ${bp.breakpoint}px) ${bp.size}`;
    }
    return bp.size; // Default size without media query
  });

  return sizesParts.join(', ');
}
